import type {
  LatestDataUpdate,
  ManagedCustomDashboardTabsConfig,
  QueuedDataUpdate,
} from '@/lib/api/custom-dashboard';

/** 受け付けたデータ更新。ジョブの行は launcher Lambda が作るため、開始直後は一覧に無い。 */
export interface QueuedDataUpdateEntry {
  jobId: string;
  /** 受け付けた日時（「受付中」の表示に使う）。 */
  queuedAt: string;
  /** 受け付けた時点のタブの直近ジョブ（無ければ null）。これ以外の行が直近になったら受付中をやめる。 */
  previousJobId: string | null;
}

/** タブ ID ごとの、受け付けたデータ更新。 */
export type QueuedDataUpdates = Readonly<Record<string, QueuedDataUpdateEntry>>;

/**
 * 受付中と表示する上限。起動依頼はキューで約 2 時間待つと DLQ に移り、行が作られないため、それを過ぎたら
 * 受付中の表示とポーリングをやめる。
 */
export const QUEUED_DISPLAY_LIMIT_MS = 3 * 60 * 60 * 1000;

/** 開始応答と、受け付けた時点のタブの直近ジョブから受付中のエントリを作る。 */
export function queuedEntry(
  queued: QueuedDataUpdate,
  previousJobId: string | null,
  now: Date = new Date(),
): QueuedDataUpdateEntry {
  return { jobId: queued.jobId, queuedAt: now.toISOString(), previousJobId };
}

/**
 * 受付中のタブ ID。受け付けた時点の直近ジョブのまま（自分の行も他のジョブの行もまだ現れていない）のタブだけを
 * 数える。行が現れたら（成功・失敗・他ユーザーのジョブを問わず）受付中ではなくなり、その後に戻ることもない。
 * タブが一覧から消えたもの・表示の上限を過ぎたものも除く。
 */
export function pendingQueuedTabIds(
  data: ManagedCustomDashboardTabsConfig | undefined,
  queued: QueuedDataUpdates,
  now: number = Date.now(),
): string[] {
  if (!data) return [];
  return data.tabs
    .filter((tab) => {
      const entry = queued[tab.tabId];
      if (entry === undefined) return false;
      const latestJobId = tab.latestDataUpdate?.jobId ?? null;
      return (
        latestJobId === entry.previousJobId &&
        now - Date.parse(entry.queuedAt) < QUEUED_DISPLAY_LIMIT_MS
      );
    })
    .map((tab) => tab.tabId);
}

/**
 * 受付中のタブの直近ジョブを `queued` に置き換えた一覧を返す（表示・データ更新ボタンの無効化・ポーリングに使う）。
 * 受付中のタブが無ければ元の一覧をそのまま返す。
 */
export function withQueuedDataUpdates(
  data: ManagedCustomDashboardTabsConfig,
  queued: QueuedDataUpdates,
  now: number = Date.now(),
): ManagedCustomDashboardTabsConfig {
  const pending = new Set(pendingQueuedTabIds(data, queued, now));
  if (pending.size === 0) return data;
  return {
    ...data,
    tabs: data.tabs.map((tab) => {
      if (!pending.has(tab.tabId)) return tab;
      const entry = queued[tab.tabId];
      const latestDataUpdate: LatestDataUpdate = {
        jobId: entry.jobId,
        status: 'queued',
        // 受付中は UI から開始したジョブだけ（定期バッチの起動は行ができてから一覧に出る）。
        trigger: 'force',
        createdAt: entry.queuedAt,
        finishedAt: null,
        resultRevision: null,
        error: null,
      };
      return { ...tab, latestDataUpdate };
    }),
  };
}
